import React, {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
} from "react";

const getModalElement = async (modalId) => {
  const existingModal = document.getElementById(modalId);
  if (
    existingModal &&
    (typeof existingModal.showOverlay === "function" ||
      typeof existingModal.hideOverlay === "function")
  ) {
    return existingModal;
  }

  if (typeof customElements !== "undefined" && !customElements.get("s-modal")) {
    await Promise.race([
      customElements.whenDefined("s-modal"),
      new Promise((resolve) => {
        setTimeout(resolve, 1000);
      }),
    ]);
  }

  return document.getElementById(modalId);
};

const normalizeActions = (actions) => {
  if (!actions) {
    return [];
  }
  return Array.isArray(actions) ? actions : [actions];
};

/**
 * Polaris Modal compatible wrapper around Shopify App Home s-modal.
 * Pass existing modal body as children; footer actions map to s-button slots.
 */
const ShopifyModal = forwardRef(
  (
    {
      id,
      open = false,
      onClose,
      heading,
      title,
      size = "base",
      padding = "base",
      accessibilityLabel,
      primaryAction,
      secondaryActions,
      showCloseButton = true,
      closeText = "Close",
      children,
    },
    ref
  ) => {
    const generatedId = useId().replace(/:/g, "");
    const modalId = id || `shopify-modal-${generatedId}`;
    const modalRef = useRef(null);
    const suppressCloseSideEffects = useRef(false);
    const modalWasVisibleRef = useRef(false);
    const closedByUserRef = useRef(false);
    const isHandlingCloseRef = useRef(false);
    const onCloseRef = useRef(onClose);

    onCloseRef.current = onClose;

    useImperativeHandle(ref, () => ({
      hideOverlay: () => {
        if (typeof modalRef.current?.hideOverlay === "function") {
          modalRef.current.hideOverlay();
        }
      },
      showOverlay: () => {
        if (typeof modalRef.current?.showOverlay === "function") {
          modalRef.current.showOverlay();
        }
      },
      getElement: () => modalRef.current,
    }));

    useEffect(() => {
      let isActive = true;
      let modalElement = null;

      const handleHide = () => {
        if (suppressCloseSideEffects.current) {
          suppressCloseSideEffects.current = false;
          return;
        }
        if (isHandlingCloseRef.current) {
          return;
        }
        isHandlingCloseRef.current = true;
        closedByUserRef.current = true;
        onCloseRef.current?.();
        isHandlingCloseRef.current = false;
      };

      getModalElement(modalId).then((el) => {
        if (!el || !isActive) {
          return;
        }
        modalElement = el;
        modalElement.addEventListener("hide", handleHide);
      });

      return () => {
        isActive = false;
        modalElement?.removeEventListener("hide", handleHide);
      };
    }, [modalId]);

    useEffect(() => {
      let isActive = true;

      if (!open && modalWasVisibleRef.current) {
        suppressCloseSideEffects.current = !closedByUserRef.current;
      }

      getModalElement(modalId).then((modal) => {
        if (!modal || !isActive) {
          return;
        }

        if (open) {
          modalWasVisibleRef.current = true;
          if (typeof modal.showOverlay === "function") {
            modal.showOverlay();
          }
        } else if (modalWasVisibleRef.current) {
          if (closedByUserRef.current) {
            closedByUserRef.current = false;
            return;
          }
          if (typeof modal.hideOverlay === "function") {
            modal.hideOverlay();
          }
        }
      });

      return () => {
        isActive = false;
      };
    }, [open, modalId]);

    const secondaryActionList = normalizeActions(secondaryActions);
    const shouldRenderDefaultClose =
      showCloseButton && secondaryActionList.length === 0;

    const modalHeading = heading || title;

    return (
      <s-modal
        id={modalId}
        ref={modalRef}
        heading={modalHeading}
        size={size}
        padding={padding}
        accessibilityLabel={accessibilityLabel}
      >
        {open ? children : null}

        {secondaryActionList.map((action, index) => {
          const { content, onAction, disabled, loading, tone = "auto" } = action;

          if (onAction) {
            return (
              <s-button
                key={`secondary-${index}-${content}`}
                slot="secondary-actions"
                variant="secondary"
                tone={tone}
                disabled={disabled || undefined}
                loading={loading || undefined}
                onClick={onAction}
              >
                {content}
              </s-button>
            );
          }

          return (
            <s-button
              key={`secondary-${index}-${content}`}
              slot="secondary-actions"
              variant="secondary"
              tone={tone}
              disabled={disabled || undefined}
              loading={loading || undefined}
              commandFor={modalId}
              command="--hide"
            >
              {content}
            </s-button>
          );
        })}

        {shouldRenderDefaultClose && (
          <s-button
            slot="secondary-actions"
            commandFor={modalId}
            command="--hide"
          >
            {closeText}
          </s-button>
        )}

        {primaryAction && (
          <s-button
            slot="primary-action"
            variant="primary"
            tone={primaryAction.destructive ? "critical" : primaryAction.tone || "auto"}
            disabled={primaryAction.disabled || undefined}
            loading={primaryAction.loading || undefined}
            onClick={primaryAction.onAction}
          >
            {primaryAction.content}
          </s-button>
        )}
      </s-modal>
    );
  }
);

ShopifyModal.displayName = "ShopifyModal";

export default ShopifyModal;
